import { ChevronRightIcon } from '@chakra-ui/icons'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Icon,
  Flex,
  Tooltip,
  Box,
  BreadcrumbSeparator,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  VStack,
  Button,
  Divider,
} from '@chakra-ui/react'
import { Link } from 'react-router-dom'
import { Base64 } from 'js-base64'
import { AiFillHome } from 'react-icons/ai'
import { BsFillArrowLeftCircleFill } from 'react-icons/bs'
import { RiFolderFill } from 'react-icons/ri'
import { ReactElement } from 'react'
import { BsThreeDots } from 'react-icons/bs'
import { MdCollections } from 'react-icons/md'
import type { ViewableParentSuccessResponse } from '../../../interfaces'
import { useGetQueryParams } from '../../../hooks/useGetQueryParams'
import { useTranslation } from 'react-i18next'
import { useHistory } from "react-router-dom";
import { colors } from '../../../../ui-kit'

const FileDetailBreadcrumbWrapper = ({
  filePath,
  viewableParent,
}: {
  filePath: string
  viewableParent: ViewableParentSuccessResponse | undefined
}): ReactElement => {

  let folderPath = '/'
  const history = useHistory();
  const filePathArr = filePath.split('/')
  const { t } = useTranslation()

  const queryFromUrl = useGetQueryParams()

  for (let i = 1; i < filePathArr.length - 2; i++) {
    folderPath = `${folderPath}${filePathArr[i]}/`
  }
  folderPath = `${folderPath}${filePathArr[filePathArr.length - 2]}`
  const pathBase = viewableParent?.mediaCollection
    ? '/media-collections'
    : '/media-library'
  let unViewableBasePath = ''
  let viewableFolderPath = ''
  if (viewableParent?.asset?.assetPath) {
    const index = viewableParent.asset.assetPath.lastIndexOf('/')
    viewableFolderPath = folderPath.slice(index, folderPath.length)
    unViewableBasePath = folderPath.slice(0, index)
  } else {
    viewableFolderPath = folderPath
  }

  const [, ...pathComponents] =
    viewableFolderPath !== '/' ? viewableFolderPath.split('/') : []

  const crumbs = [
    viewableParent?.mediaCollection
      ? {
          label: 'All collections',
          href: queryFromUrl.includes('view=LIST')
            ? `${pathBase}?view=LIST`
            : pathBase,
          icon: AiFillHome,
        }
      : {
          label: 'Home',
          href: `${pathBase}${queryFromUrl as string}`,
          icon: AiFillHome,
        },
  ]
  if (viewableParent?.mediaCollection) {
    crumbs.push({
      label: viewableParent.mediaCollection.name,
      href: `${pathBase}/${viewableParent.mediaCollection.mediaCollectionId}/${
        queryFromUrl as string
      }`,
      icon: MdCollections,
    })
  }
  crumbs.push(
    ...pathComponents.map((x, i) => {
      const pathBase64 = Base64.encode(
        `${unViewableBasePath}/${pathComponents.slice(0, i + 1).join('/')}`,
        true,
      )

      return {
        label: x,
        href: `${pathBase}/${
          viewableParent?.mediaCollection
            ? `${viewableParent.mediaCollection.mediaCollectionId}/`
            : ''
        }${pathBase64}${queryFromUrl as string}`,
        icon: RiFolderFill,
      }
    }),
  )
  return (
    <Flex
      width="100%"
      justifyContent="start"
      alignItems={'center'}
      gap="1em"
      flexWrap={'wrap'}
    >
      {window?.isMediaLibraryWidget && (
        <>
          <Tooltip label="Back to search result" aria-label="A tooltip">
            <Button
              colorScheme="appSlate"
              leftIcon={<Icon as={BsFillArrowLeftCircleFill} />}
              fontWeight="500"
              variant="link"
              borderRightRadius="0"
              onClick={() => history?.goBack()}
              marginBottom="0.28em"
            >
              {t('back', 'Back')}
            </Button>
          </Tooltip>
          <Divider
            orientation="vertical"
            borderColor={colors.appPaleLilac}
            marginBottom="0.28em"
            opacity={1}
          />
        </>
      )}
      <Breadcrumb
        spacing="8px"
        separator={<ChevronRightIcon color="appSlate" />}
      >
        {Number(crumbs.length) <= 4 ? (
          crumbs.map(x => (
            <BreadcrumbItem key={x.href}>
              <Icon as={x.icon} boxSize={5} color="appSlate" mr={2} />
              <Tooltip key={x.href} label={x.label}>
                <BreadcrumbLink
                  as={Link}
                  to={x.href}
                  color="appSlate"
                  fontWeight="medium"
                  maxW="8em"
                  isTruncated
                >
                  {x.label}
                </BreadcrumbLink>
              </Tooltip>
            </BreadcrumbItem>
          ))
        ) : (
          <>
            <BreadcrumbItem key={crumbs[0].href}>
              <Icon as={crumbs[0].icon} boxSize={5} color="appSlate" mr={2} />
              <BreadcrumbLink
                as={Link}
                to={crumbs[0].href}
                color="appSlate"
                fontWeight="medium"
                maxW="8em"
                isTruncated
              >
                {crumbs[0].label}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator
              margin="0em 0.5em 0.5em 0.5em"
              as={ChevronRightIcon}
            />
            <BreadcrumbItem>
              <BreadcrumbLink fontWeight="medium">
                <Popover trigger="hover">
                  <PopoverTrigger>
                    <IconButton
                      aria-label="More Folders"
                      icon={<Icon as={BsThreeDots} />}
                      variant="link"
                      boxShadow="none"
                      marginX="0"
                      paddingX="0"
                      minW="min-content"
                      color="appSlate"
                    />
                  </PopoverTrigger>
                  <PopoverContent maxWidth="unset" width="unset" maxH="200px" overflowY="auto">
                    <PopoverArrow />
                    <PopoverBody padding="0">
                      <VStack paddingY={1}>
                        {crumbs.slice(1, crumbs.length - 1).map(data => (
                          <Box
                            key={data.href}
                            as={Link}
                            to={data.href}
                            paddingX={2}
                            paddingY={2}
                            width="100%"
                            _hover={{
                              backgroundColor: 'appGrey',
                            }}
                            fontWeight="normal"
                            color="appDarkIndigo"
                          >
                            {data.label}
                          </Box>
                        ))}
                      </VStack>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator
              margin="0em 0.5em 0.5em 0.5em"
              as={ChevronRightIcon}
            />
            <BreadcrumbItem key={crumbs[crumbs.length - 1].href}>
              <Icon
                as={crumbs[crumbs.length - 1].icon}
                boxSize={5}
                color="appSlate"
                mr={2}
              />
              <Tooltip label={crumbs[crumbs.length - 1].label}>
                <BreadcrumbLink
                  as={Link}
                  to={crumbs[crumbs.length - 1].href}
                  color="appSlate"
                  fontWeight="medium"
                  maxW="8em"
                  isTruncated
                >
                  {crumbs[crumbs.length - 1].label}
                </BreadcrumbLink>
              </Tooltip>
            </BreadcrumbItem>
          </>
        )}
      </Breadcrumb>
    </Flex>
  )
}

export default FileDetailBreadcrumbWrapper
